import { useEffect, useState } from 'react'
import Dialog from '@mui/material/Dialog'
import DialogTitle from '@mui/material/DialogTitle'
import DialogContent from '@mui/material/DialogContent'
import DialogActions from '@mui/material/DialogActions'
import TextField from '@mui/material/TextField'
import Button from '@mui/material/Button'
import Alert from '@mui/material/Alert'
import FormControlLabel from '@mui/material/FormControlLabel'
import Switch from '@mui/material/Switch'
import InputAdornment from '@mui/material/InputAdornment'
import { api } from '../../../shared/services/API'
import { getBackendError } from '../../../shared/utils/formErrors'
import type { Addon } from '../subscricoesModel'

interface Props {
    open: boolean
    initial: Addon | null
    onClose: () => void
    onSaved: () => void
}

export default function AddonFormDialog({ open, initial, onClose, onSaved }: Props) {
    const [nome, setNome] = useState('')
    const [preco, setPreco] = useState('0')
    const [activo, setActivo] = useState(true)
    const [apiError, setApiError] = useState('')
    const [submitting, setSubmitting] = useState(false)

    useEffect(() => {
        if (!open) return
        // eslint-disable-next-line react-hooks/set-state-in-effect -- reset dos campos quando o diálogo abre
        setApiError('')
        setNome(initial?.nome ?? '')
        setPreco(String(initial?.preco ?? 0))
        setActivo(initial?.activo ?? true)
    }, [open, initial])

    const submit = async () => {
        if (!initial) return
        const nomeLimpo = nome.trim()
        if (!nomeLimpo) { setApiError('O nome é obrigatório.'); return }
        const precoNum = Number(preco)
        if (Number.isNaN(precoNum) || precoNum < 0) { setApiError('Preço inválido.'); return }

        setSubmitting(true)
        setApiError('')
        try {
            await api.put(`/api/admin/addons/${initial.id}`, { nome: nomeLimpo, preco: precoNum, activo })
            onSaved()
        } catch (err) {
            setApiError(getBackendError(err)?.message ?? 'Não foi possível guardar o add-on.')
        } finally {
            setSubmitting(false)
        }
    }

    return (
        <Dialog open={open} onClose={submitting ? undefined : onClose} maxWidth="xs" fullWidth>
            <DialogTitle sx={{ fontWeight: 700 }}>Editar add-on</DialogTitle>
            <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
                {apiError && <Alert severity="error" sx={{ borderRadius: 2 }}>{apiError}</Alert>}

                <TextField
                    label="Nome"
                    autoFocus
                    value={nome}
                    onChange={(e) => setNome(e.target.value)}
                    sx={{ mt: 2 }}
                    fullWidth
                />
                <TextField
                    label="Preço por unidade / ciclo"
                    type="number"
                    value={preco}
                    onChange={(e) => setPreco(e.target.value)}
                    helperText="Somado ao preço da subscrição, multiplicado pelas unidades extra contratadas."
                    slotProps={{
                        input: { endAdornment: <InputAdornment position="end">MT</InputAdornment> },
                        htmlInput: { min: 0, step: '0.01' },
                    }}
                    fullWidth
                />
                <FormControlLabel
                    control={<Switch checked={activo} onChange={(e) => setActivo(e.target.checked)} />}
                    label={activo ? 'Disponível para novas subscrições' : 'Escondido de novas subscrições'}
                />
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 2 }}>
                <Button onClick={onClose} disabled={submitting}>Cancelar</Button>
                <Button variant="contained" onClick={submit} disabled={submitting}>
                    {submitting ? 'A guardar…' : 'Guardar'}
                </Button>
            </DialogActions>
        </Dialog>
    )
}
